/**
 * World - Manages the game world, tiles, and entities
 */

import { Vector3 } from '../math/Vector3';
import { Tile } from './Tile';
import { TileType, StructureType } from '../types/enums';
import { Entity } from '../entities/Entity';
import { Renderer } from '../core/Renderer';
import { BuildingSystem } from '../building/BuildingSystem';

// Simple noise function for terrain generation
function noise2D(x: number, y: number, seed: number): number {
  const n = Math.sin(x * 12.9898 + y * 78.233 + seed) * 43758.5453;
  return n - Math.floor(n);
}

function smoothNoise(x: number, y: number, seed: number): number {
  const corners = (
    noise2D(x - 1, y - 1, seed) + noise2D(x + 1, y - 1, seed) +
    noise2D(x - 1, y + 1, seed) + noise2D(x + 1, y + 1, seed)
  ) / 16;
  const sides = (
    noise2D(x - 1, y, seed) + noise2D(x + 1, y, seed) +
    noise2D(x, y - 1, seed) + noise2D(x, y + 1, seed)
  ) / 8;
  const center = noise2D(x, y, seed) / 4;
  return corners + sides + center;
}

function interpolatedNoise(x: number, y: number, seed: number): number {
  const intX = Math.floor(x);
  const fracX = x - intX;
  const intY = Math.floor(y);
  const fracY = y - intY;

  const v1 = smoothNoise(intX, intY, seed);
  const v2 = smoothNoise(intX + 1, intY, seed);
  const v3 = smoothNoise(intX, intY + 1, seed);
  const v4 = smoothNoise(intX + 1, intY + 1, seed);

  const i1 = v1 * (1 - fracX) + v2 * fracX;
  const i2 = v3 * (1 - fracX) + v4 * fracX;

  return i1 * (1 - fracY) + i2 * fracY;
}

function perlinNoise(x: number, y: number, seed: number, octaves: number = 4): number {
  let total = 0;
  let frequency = 0.05;
  let amplitude = 1;
  let maxValue = 0;

  for (let i = 0; i < octaves; i++) {
    total += interpolatedNoise(x * frequency, y * frequency, seed) * amplitude;
    maxValue += amplitude;
    amplitude *= 0.5;
    frequency *= 2;
  }

  return total / maxValue;
}

export type WorldType = 'overworld' | 'hell';

// Hell world color palette
const HELL_COLORS: Record<TileType, string> = {
  [TileType.Grass]: '#4a2020',    // Dark red
  [TileType.Dirt]: '#3a3a3a',     // Charcoal
  [TileType.Stone]: '#4a3a4a',    // Dark purple-gray
  [TileType.Water]: '#c44a00',    // Lava orange
  [TileType.Sand]: '#5a5a5a',     // Ash gray
  [TileType.Tree]: '#2a1a1a',     // Dead trees, dark brown
  [TileType.Rock]: '#3a2a3a',     // Dark purple rock
  [TileType.Bush]: '#4a2a2a',     // Dark red bush
  [TileType.Snow]: '#6a5a5a',     // Ashy snow
  [TileType.IronOre]: '#8a5030',  // Rusty orange-brown iron ore
};

export class World {
  private tiles: Map<string, Tile> = new Map();
  private entities: Map<number, Entity> = new Map();
  private seed: number;
  private worldSize: number;
  readonly worldType: WorldType;

  constructor(seed: number = Date.now(), worldSize: number = 100, worldType: WorldType = 'overworld') {
    this.seed = seed;
    this.worldSize = worldSize;
    this.worldType = worldType;
    this.generateWorld();
  }

  /** Get color for tile type based on world type */
  getTileColor(type: TileType): string {
    if (this.worldType === 'hell' && HELL_COLORS[type]) {
      return HELL_COLORS[type];
    }
    // Default overworld colors (from Tile.getColor())
    switch (type) {
      case TileType.Grass: return '#4a7c59';
      case TileType.Dirt: return '#8b7355';
      case TileType.Stone: return '#808080';
      case TileType.Water: return '#4a90a4';
      case TileType.Sand: return '#c2b280';
      case TileType.Tree: return '#2d5a3d';
      case TileType.Rock: return '#606060';
      case TileType.Bush: return '#4a6a2a';
      case TileType.IronOre: return '#8a6a4a';
      default: return '#333333';
    }
  }

  /** Get regrowth color based on world type */
  getRegrowthColor(tile: Tile): string {
    if (this.worldType === 'hell') {
      // Hell world regrowth colors (dark reds/browns)
      if (tile.isRegrowingBush()) {
        return '#4a2a2a';
      }
      return '#3a1a1a';
    }
    // Default overworld regrowth colors
    return tile.getRegrowthColor();
  }

  private generateWorld(): void {
    const halfSize = Math.floor(this.worldSize / 2);

    for (let x = -halfSize; x < halfSize; x++) {
      for (let y = -halfSize; y < halfSize; y++) {
        const tile = this.generateTile(x, y);
        this.tiles.set(this.getTileKey(x, y), tile);
      }
    }
  }

  private generateTile(x: number, y: number): Tile {
    const elevation = perlinNoise(x, y, this.seed);
    const moisture = perlinNoise(x + 1000, y + 1000, this.seed + 1);
    const vegetation = perlinNoise(x + 2000, y + 2000, this.seed + 2);
    // Additional noise layer for iron ore distribution
    const oreNoise = perlinNoise(x + 3000, y + 3000, this.seed + 3);

    let type: TileType;

    // Determine tile type based on noise values
    if (elevation < 0.25) {
      type = TileType.Water;
    } else if (elevation < 0.3) {
      type = TileType.Sand;
    } else if (elevation > 0.55) {
      // Rocky highlands - more rocks at lower elevations
      if (vegetation > 0.35) {
        // In hell world, some rocks become iron ore deposits
        if (this.worldType === 'hell' && oreNoise > 0.6) {
          type = TileType.IronOre;
        } else {
          type = TileType.Rock;
        }
      } else {
        // In hell world, occasional iron ore in stone areas too
        if (this.worldType === 'hell' && oreNoise > 0.75) {
          type = TileType.IronOre;
        } else {
          type = TileType.Stone;
        }
      }
    } else {
      // Normal terrain - balanced trees and bushes
      if (vegetation > 0.62) {
        // Forest areas
        type = TileType.Tree;
      } else if (moisture < 0.35) {
        type = TileType.Dirt;
      } else if (vegetation > 0.5 && moisture > 0.45) {
        // Scattered trees
        type = TileType.Tree;
      } else if (vegetation > 0.4 && vegetation <= 0.5 && moisture > 0.4) {
        // Bushes in moderate vegetation areas
        type = TileType.Bush;
      } else {
        type = TileType.Grass;
      }
    }

    const config = Tile.getDefaultConfig(type);
    return new Tile(x, y, config);
  }

  private getTileKey(x: number, y: number): string {
    return `${Math.floor(x)},${Math.floor(y)}`;
  }

  /** Get tile at world coordinates */
  getTile(x: number, y: number): Tile | null {
    return this.tiles.get(this.getTileKey(x, y)) ?? null;
  }

  /** Get tile at Vector3 position */
  getTileAt(pos: Vector3): Tile | null {
    return this.getTile(pos.x, pos.y);
  }

  /** Check if position is walkable */
  isWalkable(x: number, y: number): boolean {
    const tile = this.getTile(x, y);
    return tile !== null && tile.walkable;
  }

  /** Check if position is buildable */
  isBuildable(x: number, y: number): boolean {
    const tile = this.getTile(x, y);
    return tile !== null && tile.buildable && !tile.hasStructure();
  }

  /** Add entity to world */
  addEntity(entity: Entity): void {
    this.entities.set(entity.id, entity);
  }

  /** Remove entity from world */
  removeEntity(entityId: number): void {
    this.entities.delete(entityId);
  }

  /** Get entity by ID */
  getEntity(id: number): Entity | undefined {
    return this.entities.get(id);
  }

  /** Get all entities */
  getAllEntities(): Entity[] {
    return Array.from(this.entities.values());
  }

  /** Get entities within radius of position */
  getEntitiesInRadius(pos: Vector3, radius: number): Entity[] {
    const result: Entity[] = [];
    const radiusSq = radius * radius;

    for (const entity of this.entities.values()) {
      const dx = entity.position.x - pos.x;
      const dy = entity.position.y - pos.y;
      if (dx * dx + dy * dy <= radiusSq) {
        result.push(entity);
      }
    }

    return result;
  }

  /** Get entities of specific type */
  getEntitiesOfType<T extends Entity>(type: new (...args: unknown[]) => T): T[] {
    const result: T[] = [];
    for (const entity of this.entities.values()) {
      if (entity instanceof type) {
        result.push(entity);
      }
    }
    return result;
  }

  /** Update all entities and tile regrowth */
  update(deltaTime: number, dayLength: number = 300): void {
    for (const entity of this.entities.values()) {
      entity.update(deltaTime);
    }

    // Remove dead entities
    for (const [id, entity] of this.entities) {
      if (!entity.isActive) {
        this.entities.delete(id);
      }
    }

    // Update tile regrowth (convert deltaTime to days)
    // dayLength is seconds per game day, so deltaDays = deltaTime / dayLength
    const deltaDays = deltaTime / dayLength;
    for (const tile of this.tiles.values()) {
      tile.updateRegrowth(deltaDays);
    }
  }

  /** Render visible tiles, structures, and entities (depth sorted) */
  render(renderer: Renderer, viewportCenter: Vector3, viewRadius: number, buildingSystem?: BuildingSystem): void {
    const minX = Math.floor(viewportCenter.x - viewRadius);
    const maxX = Math.ceil(viewportCenter.x + viewRadius);
    const minY = Math.floor(viewportCenter.y - viewRadius);
    const maxY = Math.ceil(viewportCenter.y + viewRadius);

    // Collect tiles, structures, and entities for depth sorting
    interface RenderItem {
      depth: number;
      depthOffset: number; // For layering items at same position
      render: () => void;
    }
    const renderItems: RenderItem[] = [];

    // Build a set of floor positions to skip terrain rendering
    const floorPositions = new Set<string>();
    if (buildingSystem) {
      for (const structure of buildingSystem.getAllStructures()) {
        if (structure.blueprint.structureType === StructureType.Floor) {
          const key = `${Math.floor(structure.position.x)},${Math.floor(structure.position.y)}`;
          floorPositions.add(key);
        }
      }
    }

    // Add tiles (depthOffset 0 - render first at each position)
    for (let y = minY; y <= maxY; y++) {
      for (let x = minX; x <= maxX; x++) {
        // Skip ALL terrain rendering if there's a floor structure here
        const posKey = `${x},${y}`;
        if (floorPositions.has(posKey)) {
          continue;
        }

        const tile = this.getTile(x, y);
        if (tile) {
          const depth = x + y;
          const worldPos = new Vector3(x, y, 0);
          const height = tile.getHeight();

          if (height > 0) {
            // Render as block
            renderItems.push({
              depth,
              depthOffset: 0,
              render: () => {
                const color = this.getTileColor(tile.type);
                const darkerColor = this.darkenColor(color, 0.7);
                const darkestColor = this.darkenColor(color, 0.5);
                renderer.drawIsometricBlock(worldPos, height, color, darkerColor, darkestColor);
              }
            });
          } else if (tile.isRegrowing()) {
            // Render regrowing tree as small block on grass
            renderItems.push({
              depth,
              depthOffset: 0,
              render: () => {
                // First render grass tile
                renderer.drawIsometricTile(worldPos, this.getTileColor(tile.type));
                // Then render the sapling/young tree
                const regrowthHeight = tile.getRegrowthHeight();
                const regrowthColor = this.getRegrowthColor(tile);
                const darkerColor = this.darkenColor(regrowthColor, 0.7);
                const darkestColor = this.darkenColor(regrowthColor, 0.5);
                renderer.drawIsometricBlock(worldPos, regrowthHeight, regrowthColor, darkerColor, darkestColor);
              }
            });
          } else {
            // Render as flat tile
            renderItems.push({
              depth,
              depthOffset: 0,
              render: () => renderer.drawIsometricTile(worldPos, this.getTileColor(tile.type))
            });
          }
        }
      }
    }

    // Add structures (floors at depthOffset 1, others at depthOffset 3)
    if (buildingSystem) {
      for (const structure of buildingSystem.getAllStructures()) {
        const depth = structure.position.x + structure.position.y;
        const isFloor = structure.blueprint.structureType === StructureType.Floor;
        renderItems.push({
          depth,
          depthOffset: isFloor ? 1 : 3, // Floors render after tiles, other structures after entities
          render: () => structure.render(renderer)
        });
      }
    }

    // Add entities (depthOffset 2 - render after floors, before other structures)
    for (const entity of this.entities.values()) {
      const depth = entity.position.x + entity.position.y;
      renderItems.push({
        depth,
        depthOffset: 2,
        render: () => entity.render(renderer)
      });
    }

    // Sort by depth, then by depthOffset (painter's algorithm)
    renderItems.sort((a, b) => {
      if (a.depth !== b.depth) return a.depth - b.depth;
      return a.depthOffset - b.depthOffset;
    });

    // Render all
    for (const item of renderItems) {
      item.render();
    }
  }

  private darkenColor(hex: string, factor: number): string {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgb(${Math.floor(r * factor)}, ${Math.floor(g * factor)}, ${Math.floor(b * factor)})`;
  }

  /** Get world seed */
  getSeed(): number {
    return this.seed;
  }

  /** Get world size */
  getWorldSize(): number {
    return this.worldSize;
  }

  /** Find nearest tile of type */
  findNearestTileOfType(pos: Vector3, type: TileType, maxRadius: number = 20): Tile | null {
    let nearest: Tile | null = null;
    let nearestDistSq = maxRadius * maxRadius;

    for (let r = 0; r <= maxRadius; r++) {
      for (let dx = -r; dx <= r; dx++) {
        for (let dy = -r; dy <= r; dy++) {
          if (Math.abs(dx) !== r && Math.abs(dy) !== r) continue;

          const tile = this.getTile(pos.x + dx, pos.y + dy);
          if (tile && tile.type === type) {
            const distSq = dx * dx + dy * dy;
            if (distSq < nearestDistSq) {
              nearestDistSq = distSq;
              nearest = tile;
            }
          }
        }
      }
      if (nearest) break;
    }

    return nearest;
  }

  /** Get all modified tiles for saving */
  getModifiedTiles(): { x: number; y: number; type: TileType; resourceAmount: number; regrowthStage?: number; regrowthTimer?: number; wasTree?: boolean; wasBush?: boolean }[] {
    const modified: { x: number; y: number; type: TileType; resourceAmount: number; regrowthStage?: number; regrowthTimer?: number; wasTree?: boolean; wasBush?: boolean }[] = [];

    for (const tile of this.tiles.values()) {
      if (tile.isModified()) {
        const tileData: { x: number; y: number; type: TileType; resourceAmount: number; regrowthStage?: number; regrowthTimer?: number; wasTree?: boolean; wasBush?: boolean } = {
          x: tile.x,
          y: tile.y,
          type: tile.type,
          resourceAmount: tile.resourceAmount
        };

        // Include regrowth data if tile is regrowing
        if (tile.isRegrowing()) {
          tileData.regrowthStage = tile.regrowthStage;
          tileData.regrowthTimer = tile.regrowthTimer;
          tileData.wasTree = tile.getWasTree();
          tileData.wasBush = tile.getWasBush();
        }

        modified.push(tileData);
      }
    }

    return modified;
  }

  /** Apply saved tile modifications */
  applyModifiedTiles(tiles: { x: number; y: number; type: TileType; resourceAmount: number; regrowthStage?: number; regrowthTimer?: number; wasTree?: boolean; wasBush?: boolean }[]): void {
    // Valid tile types for validation
    const validTypes = Object.values(TileType);

    for (const tileData of tiles) {
      // Skip invalid/corrupted tile data from old saves
      if (!tileData.type || !validTypes.includes(tileData.type)) {
        console.warn(`Skipping invalid tile data at (${tileData.x}, ${tileData.y}): type=${tileData.type}`);
        continue;
      }

      const tile = this.getTile(tileData.x, tileData.y);
      if (tile) {
        tile.type = tileData.type;
        tile.resourceAmount = tileData.resourceAmount;

        // Restore regrowth state if present
        if (tileData.regrowthStage !== undefined) {
          tile.regrowthStage = tileData.regrowthStage;
          tile.regrowthTimer = tileData.regrowthTimer ?? 0;
          // Restore wasTree/wasBush flags for regrowth to work correctly
          if (tileData.wasTree !== undefined) {
            tile.setWasTree(tileData.wasTree);
          }
          if (tileData.wasBush !== undefined) {
            tile.setWasBush(tileData.wasBush);
          }
        }

        // Update tile properties based on type
        if (tileData.type === TileType.Grass || tileData.type === TileType.Stone ||
            tileData.type === TileType.Dirt || tileData.type === TileType.Sand) {
          tile.walkable = true;
          tile.buildable = true;
          tile.resourceType = null;
        }
      }
    }
  }
}
